import { useCallback, useEffect, useState } from "react";
import { useLocalSearchParams } from "expo-router";
import { ActivityIndicator, Linking, Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { slugSchema } from "@tabletop/shared";

import { fetchGame, type ApiGameDetail, type Credit } from "../../src/api";
import { Cover, coverCreditFor } from "../../src/cover";
import { LogPlay } from "../../src/log-play";
import { ShelfControl } from "../../src/shelf-control";
import { formatDesigners, formatPlayerCount, formatPlayingTime } from "../../src/format";
import { colour, type } from "../../src/theme";

export default function GameScreen() {
  const { slug } = useLocalSearchParams<{ slug: string }>();

  // A runtime value from packages/shared, not just a type. Type imports are
  // erased before bundling, so they cannot prove the client consumes the
  // contract (ADR-0022). Rejecting here also saves a round trip the API would
  // answer with a 400.
  const valid = slugSchema.safeParse(slug).success;

  const [game, setGame] = useState<ApiGameDetail | null>(null);
  // Bumped when a play is logged: the rating on the shelf is projected
  // from sessions, so it changes underneath the control.
  const [shelfVersion, setShelfVersion] = useState(0);
  const onLogged = useCallback(() => setShelfVersion((n) => n + 1), []);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!valid) return;
    const controller = new AbortController();

    fetchGame(slug, controller.signal)
      .then((found) => {
        if (!controller.signal.aborted) setGame(found);
      })
      .catch((cause: Error) => {
        if (controller.signal.aborted || cause.name === "AbortError") return;
        setError(cause.message);
      });

    return () => controller.abort();
  }, [slug, valid]);

  if (!valid) return <Message title="Not a valid game" body={`“${slug}” is not a slug.`} />;
  if (error) return <Message title="Could not load that game" body={error} />;
  if (!game) return <ActivityIndicator style={styles.spinner} color={colour.inkMuted} />;

  return (
    <ScrollView contentContainerStyle={styles.screen}>
      <Cover
        url={game.imageUrl}
        credit={coverCreditFor(game.attribution)}
        title={game.title}
      />

      <Text style={styles.title}>{game.title}</Text>

      <ShelfControl slug={game.slug} version={shelfVersion} />

      <LogPlay slug={game.slug} onLogged={onLogged} />

      <Field label="Designers" value={formatDesigners(game, 4)} />
      <Field label="Players" value={formatPlayerCount(game)} />
      <Field label="Playing time" value={formatPlayingTime(game)} />
      <Field
        label="Published"
        value={game.yearPublished == null ? "Unknown" : String(game.yearPublished)}
      />
      <Field label="Weight" value={game.weight == null ? "Unknown" : `${game.weight} / 5`} />
      {game.mechanics.length > 0 && (
        <Field label="Mechanics" value={game.mechanics.join(", ")} />
      )}
      {game.themes.length > 0 && <Field label="Themes" value={game.themes.join(", ")} />}
      {game.publishers.length > 0 && (
        <Field label="Publishers" value={game.publishers.join(", ")} />
      )}

      <Attribution credits={game.attribution} />
    </ScrollView>
  );
}

/**
 * Crediting the sources that require it (WI-072, ADR-0016).
 *
 * Wikidata is CC0 and obliges nothing, so most games show nothing here. CC
 * BY-SA material obliges us to name the source and the licence wherever it
 * appears -- which makes this a rendering requirement rather than a footnote,
 * and the reason provenance is per field rather than per record.
 *
 * The API sends the credits already grouped, and refuses to build one it
 * cannot attribute; the database refuses to hold the row at all. This is the
 * last of the three, and the only one a person ever sees.
 */
function Attribution({ credits }: { credits: Credit[] }) {
  // The cover carries its own credit, immediately under the picture. Repeating
  // it here would credit the same photograph twice on one screen.
  const rest = credits.filter((c) => !c.fields.includes("imageUrl"));
  if (rest.length === 0) return null;

  return (
    <View style={styles.credits}>
      {rest.map((credit) => (
        <Pressable key={credit.url} onPress={() => void Linking.openURL(credit.url)}>
          <Text style={styles.credit}>
            {credit.fields.join(", ")} from{" "}
            {/*
              The creator first when there is one: CC BY asks us to credit the
              person, and the platform is where the work happens to live. The
              footer omitted this entirely, which would have been incomplete
              attribution for any image that reached it.
            */}
            {credit.author === undefined ? "" : credit.author + " via "}
            {credit.source === "wikipedia" ? "Wikipedia" : credit.source}, {credit.licence}
          </Text>
        </Pressable>
      ))}
    </View>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.field}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{value}</Text>
    </View>
  );
}

function Message({ title, body }: { title: string; body: string }) {
  return (
    <View style={styles.screen}>
      <Text style={styles.title}>{title}</Text>
      <Text style={styles.value}>{body}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { padding: 16, gap: 12 },
  spinner: { marginTop: 32 },
  title: { ...type.display, fontWeight: "700", color: colour.ink },
  field: { gap: 2 },
  label: { ...type.label, textTransform: "uppercase", color: colour.inkFaint, letterSpacing: 0.5 },
  value: { ...type.body, color: colour.ink },
  credits: {
    marginTop: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: colour.line,
    gap: 4,
  },
  // Quiet, and legible. An obligation rendered in 8pt grey is an obligation
  // met on paper only.
  credit: { ...type.label, color: colour.inkMuted, textDecorationLine: "underline" },
});
